import React, { useCallback, useEffect, useState } from "react";
import { theme } from "../../../App";
import Typography from "@mui/material/Typography";
import { Box, Button, Checkbox, Paper } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
    ClientsDropdown,
    DatasetTypesDropdown,
    DatetimeDropdown,
    GroupDropdown,
    ProjectsDropdown,
} from "./DropdownV2";
import { calibrationService } from "../../../services/CalibrationService";

function ErrorText(props: { message: string }) {
    return (
        <p
            style={{
                marginTop: theme.spacing(1),
                fontWeight: "bold",
                fontSize: "20px",
                color: "red",
            }}
        >
            {props.message}
        </p>
    );
}

export function LatestDatasetSelector() {
    const [selectedClient, setSelectedClient] = useState<string>("");
    const [selectedProject, setSelectedProject] = useState<string>("");
    const [selectedDatasetType, setSelectedDatasetType] = useState<string>("");
    const [selectedGroupKey, setSelectedGroupKey] = useState<string>("");
    const [selectedDatetime, setSelectedDatetime] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>();
    const [datasets, setDatasets] = useState<any[]>([]);
    const [selectedRowDatetime, setSelectedRowDatetime] = useState<string>("");
    const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const setErrorWithTimeout = useCallback(
        (error: string, timeoutInMs: number) => {
            setErrorMessage(error);
            setTimeout(() => setErrorMessage(undefined), timeoutInMs);
        },
        [setErrorMessage]
    );

    const setErrorWithDefaultTimeout = useCallback(
        (error: string) => setErrorWithTimeout(error, 10000),
        [setErrorWithTimeout]
    );

    // Fetch datasets when group is selected
    useEffect(() => {
        if (!selectedClient || !selectedProject || !selectedDatasetType || !selectedGroupKey) {
            setDatasets([]);
            setSelectedRowDatetime("");
            setSelectedRowIndex(-1);
            return;
        }

        const fetchData = async () => {
            setIsLoadingTable(true);
            try {
                // Fetch all dataset info in a single call
                const response = await calibrationService.getAllClientInfoByKeys(
                    selectedClient,
                    selectedProject,
                    selectedDatasetType,
                    selectedGroupKey
                );
                setDatasets(response.data || []);
            } catch (error) {
                console.error("Error fetching datasets:", error);
                setErrorWithDefaultTimeout("Failed to fetch datasets for the selected group");
            } finally {
                setIsLoadingTable(false);
            }
        };

        fetchData();
    }, [selectedClient, selectedProject, selectedDatasetType, selectedGroupKey, setErrorWithDefaultTimeout]);

    const handleSubmitLatest = async () => {
        if (!selectedRowDatetime) {
            setErrorWithDefaultTimeout("Please select a datetime to set as latest");
            return;
        }

        setIsSubmitting(true);
        try {
            await calibrationService.setSelectedDatasetAsLatest(
                selectedClient,
                selectedProject,
                selectedGroupKey,
                selectedDatasetType,
                selectedRowDatetime
            );

            // Re-fetch the data from the server to get the updated is_latest values
            const response = await calibrationService.getAllClientInfoByKeys(
                selectedClient,
                selectedProject,
                selectedDatasetType,
                selectedGroupKey
            );
            setDatasets(response.data || []);

            // Clear the selection after successful update
            setSelectedRowIndex(-1);
            setSelectedRowDatetime("");

            setErrorWithTimeout("Successfully updated latest dataset!", 5000);
        } catch (error) {
            console.error("Error setting latest dataset:", error);
            setErrorWithDefaultTimeout("Failed to set latest dataset");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            id="form-container"
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "90%",
                margin: "0 auto",
                padding: 3,
            }}
        >
            <h1 style={{ textAlign: "center" }}>Latest Dataset Selector</h1>
            <form
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    marginBottom: 2,
                }}
            >
                <ClientsDropdown
                    value={selectedClient}
                    setValue={setSelectedClient}
                    setError={setErrorWithDefaultTimeout}
                    styleClass={{ flex: 1, minWidth: "150px", mx: 2, px: 0 }}
                />
                <ProjectsDropdown
                    clientName={selectedClient}
                    value={selectedProject}
                    setValue={setSelectedProject}
                    setError={setErrorWithDefaultTimeout}
                    styleClass={{ flex: 1, minWidth: "150px", mx: 2, px: 0 }}
                />
                <DatasetTypesDropdown
                    clientName={selectedClient}
                    projectName={selectedProject}
                    value={selectedDatasetType}
                    setValue={setSelectedDatasetType}
                    setError={setErrorWithDefaultTimeout}
                    styleClass={{ flex: 1, minWidth: "150px", mx: 2, px: 0 }}
                />
                <GroupDropdown
                    clientName={selectedClient}
                    projectName={selectedProject}
                    datasetType={selectedDatasetType}
                    value={selectedGroupKey}
                    setValue={setSelectedGroupKey}
                    setError={setErrorWithDefaultTimeout}
                    styleClass={{ flex: 1, minWidth: "150px", mx: 2, px: 0 }}
                />
            </form>

            {errorMessage && <ErrorText message={errorMessage} />}

            {selectedGroupKey && datasets.length > 0 && (
                <Box sx={{ width: "100%", marginTop: theme.spacing(4) }}>
                    <Typography variant="h6" sx={{ textAlign: "center", marginBottom: theme.spacing(2) }}>
                        Available Datasets
                    </Typography>

                    {isLoadingTable ? (
                        <Typography sx={{ textAlign: "center", fontStyle: "italic" }}>
                            Loading datasets...
                        </Typography>
                    ) : (
                        <>
                            <TableContainer component={Paper}>
                                <Table sx={{ width: "100%" }} aria-label="datasets table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Client</TableCell>
                                            <TableCell>Project</TableCell>
                                            <TableCell>Dataset Type</TableCell>
                                            <TableCell>Group</TableCell>
                                            <TableCell>Datetime</TableCell>
                                            <TableCell>Is Latest</TableCell>
                                            <TableCell>Select</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {datasets.map((dataset, index) => {
                                            const isLatest = dataset.is_latest;
                                            const isSelected = index === selectedRowIndex;
                                            return (
                                                <TableRow
                                                    key={index}
                                                    sx={{
                                                        fontWeight: isLatest ? "bold" : "normal",
                                                        "&:hover": { backgroundColor: "#f5f5f5" },
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <TableCell sx={{ fontWeight: isLatest ? "bold" : "normal" }}>
                                                        {dataset.client_name}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: isLatest ? "bold" : "normal" }}>
                                                        {dataset.project_name}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: isLatest ? "bold" : "normal" }}>
                                                        {dataset.dataset_type}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: isLatest ? "bold" : "normal" }}>
                                                        {dataset.group_key}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: isLatest ? "bold" : "normal" }}>
                                                        {dataset.latest_change_dttm}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: isLatest ? "bold" : "normal" }}>
                                                        {isLatest ? "Yes" : "No"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedRowIndex(index);
                                                                    setSelectedRowDatetime(dataset.latest_change_dttm);
                                                                } else {
                                                                    setSelectedRowIndex(-1);
                                                                    setSelectedRowDatetime("");
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ display: "flex", justifyContent: "center", marginTop: theme.spacing(3) }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmitLatest}
                                    disabled={!selectedRowDatetime || isSubmitting}
                                    sx={{ minWidth: "200px" }}
                                >
                                    {isSubmitting ? "Updating..." : "Set Selected as Latest"}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {selectedDatetime && (
                <Box sx={{ width: "100%", marginTop: theme.spacing(4) }}>
                    <Typography variant="h6" sx={{ textAlign: "center", marginBottom: theme.spacing(2) }}>
                        Selected Dataset
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: "center",
                            fontFamily: "monospace",
                            fontSize: "1.1rem",
                            padding: theme.spacing(2),
                            backgroundColor: "#f5f5f5",
                            borderRadius: "4px",
                        }}
                    >
                        {selectedDatetime}
                    </Typography>
                </Box>
            )}
        </div>
    );
}

export default LatestDatasetSelector;
